// AP-049 · Multi-Company Demo Mode — Client-side tenant selector.
// Persists the active tenant in localStorage and broadcasts a global event so
// any listing page can refresh when Master switches company.
import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const TenantContext = createContext({
  tenants: [],
  currentTenant: "jms",
  currentTenantMeta: null,
  isMaster: false,
  switching: false,
  switchTenant: () => {},
  seedDemo: async () => {},
});

const STORAGE_KEY = "jms_tenant";
export const DEFAULT_TENANT = "jms";

export function TenantProvider({ children }) {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [currentTenant, setCurrentTenant] = useState(
    () => localStorage.getItem(STORAGE_KEY) || DEFAULT_TENANT
  );
  const [switching, setSwitching] = useState(false);

  // Sync localStorage on every change (used by axios interceptor).
  useEffect(() => { localStorage.setItem(STORAGE_KEY, currentTenant || DEFAULT_TENANT); }, [currentTenant]);

  // Fetch the tenants visible to the current user whenever auth state changes.
  useEffect(() => {
    if (!user) { setTenants([]); return; }
    api.get("/tenants").then(({ data }) => {
      setTenants(data.items || []);
      // If the user is not Master and the persisted tenant isn't jms, snap back.
      if (!data.is_master && currentTenant !== DEFAULT_TENANT) {
        setCurrentTenant(DEFAULT_TENANT);
      }
    }).catch(() => { setTenants([]); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const isMaster = (user?.role || "") === "master_super_admin";

  const currentTenantMeta = useMemo(
    () => tenants.find((t) => t.id === currentTenant) || {
      id: DEFAULT_TENANT, name: "JMS MULTI SERVICE LTDA", demo: false, color: "#0057B8", short: "JMS",
    },
    [tenants, currentTenant]
  );

  const switchTenant = useCallback((next) => {
    if (!next || next === currentTenant) return;
    setSwitching(true);
    setCurrentTenant(next);
    // Give axios interceptor a tick to pick up the new value before we reload.
    setTimeout(() => {
      // Fire a global event; any listing page listening to it can refetch.
      window.dispatchEvent(new CustomEvent("jms:tenant-changed", { detail: { tenant: next } }));
      setSwitching(false);
    }, 30);
  }, [currentTenant]);

  const seedDemo = useCallback(async () => {
    const { data } = await api.post("/tenants/seed-demo");
    return data;
  }, []);

  const value = { tenants, currentTenant, currentTenantMeta, isMaster, switching, switchTenant, seedDemo };
  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant() { return useContext(TenantContext); }
